import { Paper } from '@mui/material';
import { useIsSupplier } from 'shared/permissions/useIsSupplier';
import { useIsTechnician } from 'shared/permissions/useIsTechnician';
import { useIsOperator } from 'shared/permissions/useIsOperator';
import TenantPage from 'layouts/TenantPage';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DeliveriesWidget,
  DeliveryExpectedWidget,
  ExpiringContracts,
  ExpiringMaintenancesWidget,
  ExpiringProducts,
  ExpiringToday,
  LisStatusWidget,
  NotificationWidget,
  OpenTickets,
  PurchaseRequestsAutomatic,
  PurchaseRequestsToApprove,
  TicketToApprove,
  UnderstockProducts
} from './widgets';

const widgetCardSx = {
  p: 2.5,
  height: '100%',
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'divider',
  backgroundColor: 'background.paper'
} as const;

function WidgetCard({ children, minHeight }: { children: ReactNode; minHeight?: number | string }) {
  return (
    <Paper elevation={0} sx={{ ...widgetCardSx, minHeight }}>
      {children}
    </Paper>
  );
}

function DashboardWidgets({ showTicketApprove }: { showTicketApprove: boolean }) {
  return (
    <>
      <div
        className={`grid grid-cols-1 gap-4 ${
          showTicketApprove ? 'md:grid-cols-3' : 'md:grid-cols-2'
        }`}>
        <WidgetCard minHeight={220}>
          <DeliveriesWidget />
        </WidgetCard>
        <WidgetCard minHeight={220}>
          <OpenTickets />
        </WidgetCard>
        {showTicketApprove && (
          <WidgetCard minHeight={220}>
            <TicketToApprove />
          </WidgetCard>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <WidgetCard minHeight={260}>
          <ExpiringMaintenancesWidget />
        </WidgetCard>
        <WidgetCard minHeight={260}>
          <ExpiringProducts />
        </WidgetCard>
        <WidgetCard minHeight={260}>
          <UnderstockProducts />
        </WidgetCard>
      </div>
    </>
  );
}

function LegacyTenantDashboard() {
  return (
    <>
      <div className="w-1/6 p-5">
        <DeliveryExpectedWidget />
      </div>

      <div className="w-1/6 p-5">
        <PurchaseRequestsToApprove />
      </div>

      <div className="w-1/6 p-5">
        <PurchaseRequestsAutomatic />
      </div>

      <div className="w-1/6 p-5">
        <LisStatusWidget />
      </div>

      <div className="w-1/6 p-5">
        <TicketToApprove />
      </div>

      <div className="w-1/6 p-5">
        <OpenTickets />
      </div>

      <div className="w-full flex flex-wrap">
        <div className="w-full p-4">
          <ExpiringToday />
        </div>
      </div>

      <div className="w-full flex flex-wrap">
        <div className="w-1/4 p-4">
          <ExpiringContracts />
        </div>
        <div className="w-1/4 p-4">
          <ExpiringMaintenancesWidget />
        </div>

        <div className={'w-1/4 p-4'}>
          <ExpiringProducts />
        </div>

        <div className="w-1/4 p-4">
          <UnderstockProducts />
        </div>
      </div>

      <div className="w-full p-4">
        <DeliveriesWidget />
      </div>

      <div className="w-full p-4">
        <NotificationWidget />
      </div>
    </>
  );
}

export default function TenantHome() {
  const { t } = useTranslation();
  const translationPrefix = 'dashboard';
  const isDisabled = useIsSupplier() || useIsOperator();

  if (useIsTechnician()) {
    return (
      <TenantPage
        title={t(`${translationPrefix}.title`)}
        subtitle={t(`${translationPrefix}.subtitle`)}
        hideBreadcrumbs={true}>
        <div className="w-full flex flex-col gap-4 p-4">
          <DashboardWidgets showTicketApprove={false} />
        </div>
      </TenantPage>
    );
  }

  return (
    <TenantPage
      title={t(`${translationPrefix}.title`)}
      subtitle={t(`${translationPrefix}.subtitle`)}
      hideBreadcrumbs={true}>
      <div className="flex w-full flex-wrap">{!isDisabled && <LegacyTenantDashboard />}</div>
    </TenantPage>
  );
}
